import "./SurveyFooterComponent.scss";

import { Button } from "@fluentui/react-northstar";
import { DialogInfo } from "@microsoft/teams-js";
import { CardHelper, SurveyResultsPagingCard } from "@teams/cards";
import { ScheduleDTO, SurveyDTO } from "@teams/types/api";
import React from "react";

import { useUserInfo } from "../../../../_shared/actions/useUserInfo";
import { DisabledUpgradeButton } from "../../../../_shared/components/DisabledUpgradeButton";
import { SeriesMenu } from "../../../../_shared/components/Menus/SeriesMenu/SeriesMenu";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { isError } from "../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";
import { isMobileClient } from "../../../../_shared/utils/clientHelper";
import { clientEventTracker } from "../../../../_shared/utils/clientTelemetry";
import { startTaskErrorMessage } from "../../../../_shared/utils/startTaskErrorMessage";
import { startTask } from "../../../../_shared/utils/startTaskWrapper";
import { getAppId, getPageContext } from "../../../../_shared/utils/teamsHelpers";
import {
  canReadSurveyResults,
  getFreeUserResultsLimitText,
  isSurveyResultsRestrictedForUser,
} from "../../../../_shared/utils/userHelpers";
import { SurveyHistoryButtons } from "./SurveyHistoryButtons";
import { SurveyMenu } from "./SurveyMenu";

type Props = {
  survey: SurveyDTO;
  schedule?: ScheduleDTO;
  onChanged: (id: string) => void;
};

export const SurveyFooterComponent: React.FC<Props> = ({ survey, schedule, onChanged }) => {
  const { teamsContext } = useTeamsContext();
  const { user } = useUserInfo();

  const viewSurveyResults = async () => {
    const taskInfo: DialogInfo = {
      title: `Results: ${survey.title}`,
      width: 600,
      completionBotId: getAppId(),
    };

    if (survey.type === "quiz") {
      taskInfo.url = `https://${window.location.host}${getPageContext()}/content/quiz/${
        survey.id
      }/results`;
      taskInfo.width = 800;
      taskInfo.height = 1000;
    } else {
      const result = await surveyApi.surveyResponses(teamsContext, survey.id as string);

      if (isError(result)) {
        startTaskErrorMessage(result.message, survey.applicationType);
        return;
      }

      const card = SurveyResultsPagingCard(survey, result.responses, result.autolinkIntegrations);
      taskInfo.card = CardHelper.toJSONv12String(card);
    }

    startTask(taskInfo);
  };

  //Due to some bugs on teams mobile clients most of our buttons dont work there so just dont show anything
  if (isMobileClient(teamsContext)) {
    return null;
  }

  return (
    <>
      <div className="surveyFooter__management-controls">
        <div className="surveyFooter__autolink">
          {survey.autolinkIntegrations && survey.autolinkIntegrations.length > 0 && (
            <PollyIcon
              icon="integration-icon"
              label={`Integrations (${survey.autolinkIntegrations.length})`}
              inheritColor
            />
          )}
        </div>
        {!schedule && (
          <SurveyMenu survey={survey} onChanged={() => onChanged(survey.id as string)} />
        )}
        {schedule && (
          <SeriesMenu user={user} schedule={schedule} onChanged={() => onChanged(schedule.id)} />
        )}
      </div>
      <div className="surveyFooter">
        {!schedule && survey.state !== "pending" && (
          <>
            {!isSurveyResultsRestrictedForUser(user, survey) ? (
              <Button
                primary
                onClick={() => {
                  clientEventTracker.track("ViewAllResults clicked", user, {
                    pollyId: survey.id,
                  });

                  return viewSurveyResults();
                }}
                disabled={!canReadSurveyResults(survey)}
                className="surveyFooter__view-results-button"
                // NOTE: if this text is changed, make sure to change the text in Polly preview as well.
                content="View results"
              />
            ) : (
              <DisabledUpgradeButton
                tooltip={getFreeUserResultsLimitText(user)}
                className="surveyFooter__view-results-button"
              />
            )}
          </>
        )}
        {schedule && (
          <SurveyHistoryButtons
            scheduleId={schedule.id}
            onViewResponsesClickHandler={() => {
              clientEventTracker.track("HistoricalResponses clicked", user, {
                pollyId: survey.id,
                scheduleId: schedule.id,
              });
              return viewSurveyResults();
            }}
            allowShowResults={canReadSurveyResults(survey)}
            upgradeButton={
              isSurveyResultsRestrictedForUser(user, schedule.lastSurvey) && (
                <DisabledUpgradeButton tooltip={getFreeUserResultsLimitText(user)} />
              )
            }
          />
        )}
      </div>
    </>
  );
};
